import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI } from 'sonata-react-components';

import { Creatives } from './Creatives';
import { Goals } from './Goals';
import { Measurement } from './Measurement';
import { MediaChannel } from './MediaChannel';
import { MediaType } from './MediaType';
import { Strategy } from './Strategy';

import './searchByFilters.scss';

export type SearchByFilterGroup = 'mediaChannel' | 'pricingType' | 'creatives' | 'goal' | 'measurement' | 'strategy';

export type SearchByFilterEntry = { id: string; value: string[] };

export const LINE_ITEM_FILTER_GROUPS: SearchByFilterGroup[] = [
  'mediaChannel',
  'pricingType',
  'creatives',
  'goal',
  'measurement',
  'strategy'
];

export const INSERTION_ORDER_FILTER_GROUPS: SearchByFilterGroup[] = [
  'mediaChannel',
  'pricingType',
  'measurement',
  'strategy'
];

type Props = {
  onClose?: () => void;
  onFiltersChange: (filters: SearchByFilterEntry[]) => void;
  value: SearchByFilterEntry[];
  variant?: 'lineItem' | 'insertionOrder';
};

function getGroupValue(entries: SearchByFilterEntry[], id: string): string[] {
  return entries.find(f => f.id === id)?.value ?? [];
}

/** Order-independent equality: same filter ids, each with the same set of values. */
function areFilterListsEqual(a: SearchByFilterEntry[], b: SearchByFilterEntry[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(entry => {
    if (!b.some(f => f.id === entry.id)) {
      return false;
    }
    const otherValue = getGroupValue(b, entry.id);
    const entryValueSet = new Set(entry.value);
    return otherValue.length === entry.value.length && otherValue.every(v => entryValueSet.has(v));
  });
}

export function SearchByFilters({ onClose, onFiltersChange, value = [], variant = 'lineItem' }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const groups = variant === 'insertionOrder' ? INSERTION_ORDER_FILTER_GROUPS : LINE_ITEM_FILTER_GROUPS;

  const [draft, setDraft] = useState<SearchByFilterEntry[]>(
    value.filter(f => groups.includes(f.id as SearchByFilterGroup))
  );

  useEffect(() => {
    setDraft(value.filter(f => groups.includes(f.id as SearchByFilterGroup)));
  }, [value, groups]);

  const handleGroupChange = (filters: SearchByFilterEntry[]) => {
    setDraft(prev => {
      const ids = filters.map(f => f.id);
      const kept = prev.filter(f => !ids.includes(f.id));
      const nonEmpty = filters.filter(f => f.value.length > 0);
      return [...kept, ...nonEmpty];
    });
  };

  const handleApply = () => {
    onFiltersChange(draft);
  };

  const handleClearAll = () => {
    setDraft([]);
    onFiltersChange([]);
  };

  const appliedFilters = value.filter(f => groups.includes(f.id as SearchByFilterGroup));
  const isClearDisabled = draft.length === 0;
  const isApplyDisabled = areFilterListsEqual(draft, appliedFilters);

  return (
    <div className="searchByFilters">
      <div className="searchByFilters__main">
        <div className="searchByFilters__header">
          <span className="searchByFilters__headerTitle">
            {formatMessage({ defaultMessage: 'Search by', id: 'campaigns.advancedFilters.title' })}
          </span>
          <ButtonUI icon="fal fa-times" id="searchByFilters__close" onClick={onClose} shape="link" type="secondary" />
        </div>
        <div className="searchByFilters__content">
          {groups.includes('mediaChannel') && (
            <MediaChannel onFilter={handleGroupChange} value={getGroupValue(draft, 'mediaChannel')} />
          )}
          {groups.includes('pricingType') && (
            <MediaType onFilter={handleGroupChange} value={getGroupValue(draft, 'pricingType')} />
          )}
          {groups.includes('creatives') && (
            <Creatives onFilter={handleGroupChange} value={getGroupValue(draft, 'creatives')} />
          )}
          {groups.includes('goal') && <Goals onFilter={handleGroupChange} value={getGroupValue(draft, 'goal')} />}
          {groups.includes('measurement') && (
            <Measurement onFilter={handleGroupChange} value={getGroupValue(draft, 'measurement')} />
          )}
          {groups.includes('strategy') && (
            <Strategy onFilter={handleGroupChange} value={getGroupValue(draft, 'strategy')} />
          )}
        </div>
      </div>
      <div className="searchByFilters__actions">
        <ButtonUI
          disabled={isClearDisabled}
          icon="fa-light fa-trash"
          id="searchByFilters__clearAll"
          onClick={handleClearAll}
          shape="link"
          text={formatMessage({
            defaultMessage: 'Clear',
            id: 'campaigns.advancedFilters.clearAll'
          })}
          type="secondary"
        />
        <ButtonUI
          disabled={isApplyDisabled}
          id="searchByFilters__apply"
          onClick={handleApply}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Apply filters',
            id: 'campaigns.advancedFilters.apply'
          })}
          type="tertiary"
        />
      </div>
    </div>
  );
}
